import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Grid,
    DialogActions,
    DialogContent,
    TextField,
    CircularProgress,
    Button,
    MenuItem,
    Box
} from "@mui/material";
import { useSnackbar } from "notistack";
import http from "src/utils/httpHelper";
import Client from "src/models/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";

interface ClientFormProps {
    modalCloseCallback,
    onSuccessCallback
    item?: Client
}

const ClientForm = ({ modalCloseCallback, onSuccessCallback, item }: ClientFormProps) => {
    const { t }: { t: any } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const { handleSubmit, formState: { errors, isSubmitting, isValid }, control, reset } = useForm({
        mode: 'onTouched',
        resolver: yupResolver(Client.getRules(item)),
        defaultValues: Client.getInitValues(item)
    });

    return (
        <form noValidate onSubmit={handleSubmit(async (values) => {
            await http.request({
                method: item ? 'put' : 'post',
                url: item ? `/api/clients/${item.id}` : '/api/clients',
                data: values
            })
                .then((response) => {
                    onSuccessCallback();
                    enqueueSnackbar(response.data.message, { variant: 'success' });
                })
                .catch((error) => {
                    console.error(error);
                    enqueueSnackbar(error.message, { variant: 'error' });
                });
        })}>
            <DialogContent
                dividers
                sx={{
                    p: 3,
                }}
            >
                <Grid container spacing={3}>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="rfc"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.rfc)}
                                    fullWidth
                                    helperText={errors.rfc?.message}
                                    label={t("FIELDS.RFC")}
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="name"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.name)}
                                    fullWidth
                                    helperText={errors.name?.message}
                                    label={t("FIELDS.NAME")}
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions
                sx={{
                    p: 3,
                    display: 'flex',
                    justifyContent: 'flex-end'
                }}
            >
                <Box>
                    <Button color="secondary" onClick={modalCloseCallback}>
                        {t("GENERAL.CANCEL")}
                    </Button>
                    <Button
                        sx={{
                            ml: 2
                        }}
                        type="submit"
                        startIcon={
                            isSubmitting ? <CircularProgress size="1rem" /> : null
                        }
                        disabled={isSubmitting || !isValid}
                        variant="contained"
                    >
                        {t(!item ? "CLIENTS.CREATE" : 'CLIENTS.EDIT')}
                    </Button>
                </Box>
            </DialogActions>
        </form>
    )
}

export default ClientForm;